import {
  Grid,
  MenuItem,
  Select,
  Step as MuiStep,
  StepLabel,
  Stepper,
  TextField,
} from '@material-ui/core'
import { MultipleSelect } from 'components/controls/form/MultipleSelect'
import { Button } from 'components/facets'
import CardmarketLogo from 'img/cardmarket_blue_logo.png'
import CardTraderLogo from 'img/cardtrader-logo.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BulkSettings,
  Competitor,
  PricingStrategy,
  PRICING_BOT_SOURCE,
  Reputation,
  SellerLevel,
} from 'shared'
import { COUNTRIES } from 'utils/constants'
import { FromCountries, REPUTATION_MAP } from '../../CompetitorBox'
import { StepBox } from '../StepBox'

export interface CreateCompetitorPresenterProps {
  newName: string
  onRestart?: () => void
  onDone: (competitor: Competitor) => void
}

enum Step {
  Source = 1,
  Professionality,
  Settings,
  ExcludeUsers,
  Finalize,
}

const PLACEHOLDER = '—'

const CompetitorPreview = ({
  competitor,
  progress,
  step,
  sourceSelected,
  levelSelected,
}: {
  competitor: Competitor
  progress: Step
  step: Step
  sourceSelected: boolean
  levelSelected: boolean
}): JSX.Element => {
  const { t } = useTranslation()
  const sourceTouched = sourceSelected
  const levelTouched = levelSelected
  const detailsTouched = progress >= Step.Settings
  const excludedTouched = progress >= Step.ExcludeUsers
  const nameTouched = progress >= Step.Finalize
  const fieldClass = (touched: boolean, isActive: boolean): string => {
    const classes = ['wizard-preview__field']
    if (!touched) classes.push('wizard-preview__field--untouched')
    // Once a placeholder is filled, blink a few times so the change is noticed.
    if (touched) classes.push('wizard-preview__field--blink')
    if (isActive) classes.push('wizard-preview__field--active')
    return classes.join(' ')
  }
  const sourceLabel = sourceTouched
    ? competitor.source === PRICING_BOT_SOURCE.CARDTRADER
      ? t('common.channels.cardtrader')
      : t('common.channels.cardmarket')
    : PLACEHOLDER
  const levelLabel = levelTouched
    ? { 0: t('competitor.all'), 1: 'Professional', 2: 'Powerseller' }[competitor.level]
    : PLACEHOLDER
  const isCardtrader = sourceTouched && competitor.source === PRICING_BOT_SOURCE.CARDTRADER
  return (
    <div>
      <div className="wizard-preview__label">{t('competitor.create.previewLabel')}</div>
      <h6
        className={`wizard-preview__title ${
          step === Step.Finalize ? 'wizard-preview__field--active' : ''
        } ${nameTouched ? 'wizard-preview__field--blink' : ''}`}
      >
        {nameTouched ? competitor.name || PLACEHOLDER : PLACEHOLDER}
      </h6>
      <p className="wizard-preview__sentence">
        {t('competitor.create.previewIntro')}{' '}
        <span className={fieldClass(sourceTouched, step === Step.Source)}>{sourceLabel}</span>
        {step >= Step.Professionality && (
          <>
            {' '}
            <span className={fieldClass(levelTouched, step === Step.Professionality)}>
              {levelLabel}
            </span>{' '}
            {t('competitor.sellers')}
          </>
        )}
        {step >= Step.Settings && (
          <span className={fieldClass(detailsTouched, step === Step.Settings)}>
            {' '}
            {t('competitor.from')}{' '}
            <FromCountries
              countries={competitor.countries || []}
              anywhereText={t('competitor.fromAnywhere')}
            />
            {isCardtrader ? (
              <>
                {' '}
                {t('competitor.onlyCardtraderZero1')}{' '}
                {competitor.onlyCardtraderZero
                  ? t('competitor.onlyCardtraderZeroConsiderZeroOnly')
                  : t('competitor.onlyCardtraderZeroConsiderAll')}{' '}
                {t('competitor.onlyCardtraderZero2')}
              </>
            ) : (
              <>
                {' '}
                {t('competitor.withReputationOfAtLeast')}{' '}
                {REPUTATION_MAP[competitor.reputation || Reputation.VeryGood]}{' '}
                {t('competitor.andAtLeast')} {competitor.sellCount} {t('competitor.sales')}
              </>
            )}
          </span>
        )}
        {step >= Step.ExcludeUsers && (
          <>
            . {t('competitor.excludedUsers')}:{' '}
            <span className={fieldClass(excludedTouched, step === Step.ExcludeUsers)}>
              {(competitor.excludedUsers?.length ?? 0) > 0
                ? competitor.excludedUsers!.join(', ')
                : t('competitor.none')}
            </span>
          </>
        )}
      </p>
    </div>
  )
}

export const CreateCompetitorPresenter = ({
  newName,
  onDone,
}: CreateCompetitorPresenterProps): JSX.Element => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>(Step.Source)
  const [progress, setProgress] = useState<Step>(Step.Source)
  // Source and level have defaults required by the type, but we only treat them
  // as chosen once the user explicitly clicks — so nothing is pre-highlighted
  // and the preview/Next reflect a real selection.
  const [sourceSelected, setSourceSelected] = useState(false)
  const [levelSelected, setLevelSelected] = useState(false)

  const defaultValues = {
    _id: '',
    countries: [],
    reputation: Reputation.VeryGood,
    sellCount: 100,
    excludedUsers: [],
    name: newName,
    level: SellerLevel.Private,
    source: PRICING_BOT_SOURCE.CARDMARKET,
    onlyCardtraderZero: false,
  }

  const [competitor, setCompetitor] = useState<Competitor>(defaultValues)

  const handleChange = (
    newValue: unknown,
    attributeName?: keyof Competitor | keyof PricingStrategy | keyof BulkSettings
  ): void => {
    if (!attributeName) return
    const lastCharterDifferentOfSpace = /\S/.test(String(newValue))
    if (attributeName == 'name' && !lastCharterDifferentOfSpace) return
    setCompetitor({ ...competitor, [attributeName]: newValue })
  }

  const stepLabels = [
    t('transaction.channel'),
    t('competitor.level'),
    `${t('competitor.countries')}, ${t('competitor.reputation')}, ${t('competitor.sellCount')}`,
    t('competitor.excludedUsers'),
    t('competitor.name'),
  ]

  /**
   * Step 1 — pick the pricing data source (Cardmarket vs CardTrader).
   * Clicking only selects (visual feedback); advance with the Next button.
   */
  const stepSource = (): JSX.Element => {
    const sources = [
      {
        source: PRICING_BOT_SOURCE.CARDMARKET,
        label: t('common.channels.cardmarket'),
        logo: CardmarketLogo,
      },
      {
        source: PRICING_BOT_SOURCE.CARDTRADER,
        label: t('common.channels.cardtrader'),
        logo: CardTraderLogo,
      },
    ]
    return (
      <StepBox minWidth={0}>
        <h6>{t('competitor.create.sourceDescription')}</h6>
        <div className="mx-auto d-flex flex-column gap-2 wizard-source-options">
          {sources.map(({ source, label, logo }) => (
            <div
              key={source}
              data-testid={`box-${source}`}
              onClick={() => {
                setCompetitor({ ...competitor, source })
                setSourceSelected(true)
              }}
              className={`d-flex flex-row justify-content-between align-items-center sales-channel-box ${
                sourceSelected && competitor.source === source ? 'sales-channel-box--active' : ''
              }`}
            >
              <h6 className="m-0">{label}</h6>
              <img src={logo} alt={logo} />
            </div>
          ))}
        </div>
      </StepBox>
    )
  }

  /**
   * Step 2 — pick the seller level (All / Professional / Powerseller).
   * Powerseller is hidden when the source is CardTrader. Clicking only selects
   * (visual feedback); advance with the Next button.
   */
  const stepProfessionality = (): JSX.Element => {
    const select = (level: SellerLevel): void => {
      setCompetitor({ ...competitor, level })
      setLevelSelected(true)
    }
    const levels = [
      {
        level: SellerLevel.Private,
        label: t('competitor.all'),
        icon: 'fa-solid fa-user-group',
        testid: 'competitor-all',
      },
      {
        level: SellerLevel.Professional,
        label: 'Professional',
        icon: 'fa-solid fa-briefcase',
        testid: 'competitor-professional',
      },
      ...(competitor.source !== PRICING_BOT_SOURCE.CARDTRADER
        ? [
            {
              level: SellerLevel.Powerseller,
              label: 'Powerseller',
              icon: 'fa-solid fa-apartment',
              testid: 'competitor-powerseller',
            },
          ]
        : []),
    ]
    return (
      <StepBox minWidth={0}>
        <p>{t('competitor.tooltip.level')}</p>
        <div className="mx-auto d-flex flex-wrap gap-2 wizard-source-options">
          {levels.map(({ level, label, icon }) => (
            <div
              key={level}
              data-testid="sales-channel-box"
              onClick={() => select(level)}
              className={`d-flex flex-column justify-content-between align-items-center sales-channel-box competitor ${
                levelSelected && competitor.level === level ? 'sales-channel-box--active' : ''
              }`}
            >
              <h6 className="m-0">{label}</h6>
              <i className={`${icon} sales-channel-box__icon`} />
            </div>
          ))}
        </div>
        {competitor.source !== PRICING_BOT_SOURCE.CARDTRADER && (
          <div className="mt-3 text-muted">
            <p className="mb-1">
              <b>Professional</b>: {t('competitor.professionalHint')}
            </p>
            <p className="mb-0">
              <b>Powerseller</b>: {t('competitor.powersellerHint')}
            </p>
          </div>
        )}
      </StepBox>
    )
  }

  /**
   * Step 3 — countries (multi-select), reputation (select), sales threshold
   * (number) for Cardmarket; or only-Zero toggle for CardTrader. Manual Next.
   */
  const stepSettings = (): JSX.Element => (
    <StepBox minWidth={0}>
      <div className="mb-4">
        <ul>
          <li>
            <div className="d-flex flex-row align-items-end">
              <span className="me-2">
                {t('competitor.create.IWantToCompete')} {t('competitor.from')}
              </span>
              <div className="mx-2">
                <MultipleSelect
                  value={competitor.countries || []}
                  onChange={(v) => handleChange(v, 'countries')}
                  options={COUNTRIES.map((country) => ({
                    value: country.code,
                    label: country.name,
                  }))}
                  data-testid="competitor-country"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>

      {competitor.source !== PRICING_BOT_SOURCE.CARDTRADER ? (
        <>
          <div className="mb-4">
            <ul>
              <li>
                <div className="d-flex flex-row align-items-end">
                  <span className="me-2">
                    {t('competitor.create.IWantToCompete')} {t('competitor.create.thatHaveAtleast')}
                  </span>
                  <div className="mx-2">
                    <Select
                      value={competitor.reputation || Reputation.VeryGood}
                      onChange={(e) => handleChange(Number(e.target.value), 'reputation')}
                      data-testid="competitor-reputation"
                    >
                      {Object.keys(REPUTATION_MAP).map((key) => (
                        <MenuItem key={key} value={Number(key)}>
                          {REPUTATION_MAP[Number(key)]}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <span>{t('competitor.reputation')}</span>
                </div>
              </li>
            </ul>
          </div>
          <div className="mb-4">
            <ul>
              <li>
                <div className="d-flex flex-row align-items-end">
                  <span>
                    {t('competitor.create.IWantToCompete')} {t('competitor.create.thatHaveAtleast')}
                  </span>
                  <div className=" --narrow mx-2">
                    <TextField
                      type="number"
                      value={competitor.sellCount ?? 0}
                      onChange={(e) => handleChange(Number(e.target.value), 'sellCount')}
                      inputProps={{ min: 0 }}
                      fullWidth
                      data-testid="competitor-sales"
                    />
                  </div>
                  <span>{t('competitor.sales')}</span>
                </div>
              </li>
            </ul>
          </div>
        </>
      ) : (
        <div className="mb-4">
          <ul>
            <li>
              <div className="d-flex flex-row align-items-end">
                <span>
                  {t('competitor.create.IWantToCompete')} {t('competitor.onlyCardtraderZero1')}
                </span>
                <div className="mx-2">
                  <Select
                    value={competitor.onlyCardtraderZero ? 'true' : 'false'}
                    onChange={(e) => handleChange(e.target.value === 'true', 'onlyCardtraderZero')}
                    data-testid="competitor-onlyZero"
                    fullWidth
                  >
                    <MenuItem value="false">
                      {t('competitor.onlyCardtraderZeroConsiderAll')}
                    </MenuItem>
                    <MenuItem value="true">
                      {t('competitor.onlyCardtraderZeroConsiderZeroOnly')}
                    </MenuItem>
                  </Select>
                </div>
                <span>{t('competitor.onlyCardtraderZero2')}</span>
              </div>
            </li>
          </ul>
        </div>
      )}
    </StepBox>
  )

  /**
   * Step 4 — paste/edit the list of excluded Cardmarket usernames (one per
   * line). The competitor will ignore offers from these accounts.
   */
  const stepExcludeUsers = (): JSX.Element => (
    <StepBox minWidth={0}>
      <p className="mb-2">{t('competitor.create.excludedUsersDescription')}</p>
      <p className="mb-2">{t('competitor.create.excludedUsersDescription2')}</p>
      <p className="mb-2">{t('competitor.create.excludedUsersHint')}</p>
      <div>
        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={8}
          value={(competitor.excludedUsers || []).join('\n')}
          onChange={(e) =>
            setCompetitor({
              ...competitor,
              excludedUsers: e.target.value ? e.target.value.split('\n') : [],
            })
          }
          label={t('competitor.excludedUsers')}
          placeholder="someCardmarketUsername"
          data-testid="competitor-exludedUser"
        />
      </div>
      {Boolean(competitor.excludedUsers?.length) && (
        <p className="mb-2" data-testid="competitor-exludedUser-list">
          {t('competitor.create.weWillExclude')} <b>{competitor.excludedUsers?.join(', ')}</b>
        </p>
      )}
    </StepBox>
  )

  /**
   * Step 5 — give the competitor a final name and confirm with Done.
   */
  const stepFinalize = (): JSX.Element => (
    <StepBox minWidth={0}>
      <p className="mb-2">{t('competitor.create.almostDone')}</p>
      <div>
        <TextField
          fullWidth
          value={competitor.name}
          autoFocus
          onFocus={(event) => event.target.select()}
          onChange={(e) => setCompetitor({ ...competitor, name: e.target.value })}
          label={t('competitor.name')}
          data-testid="competitor-name"
        />
      </div>
    </StepBox>
  )

  const nextConfig = ((): {
    onClick: () => void
    isLast?: boolean
    disabled?: boolean
  } | null => {
    switch (step) {
      case Step.Source:
        return {
          onClick: () => {
            setProgress(Math.max(Step.Source + 1, progress))
            setStep(Step.Source + 1)
          },
          disabled: !sourceSelected,
        }
      case Step.Professionality:
        return {
          onClick: () => {
            setProgress(Math.max(Step.Professionality + 1, progress))
            setStep(Step.Professionality + 1)
          },
          disabled: !levelSelected,
        }
      case Step.Settings:
        return {
          onClick: () => {
            setProgress(Math.max(Step.Settings + 1, progress))
            setStep(Math.max(Step.Settings + 1, progress))
          },
        }
      case Step.ExcludeUsers:
        return {
          onClick: () => {
            setProgress(Math.max(Step.ExcludeUsers + 1, progress))
            setStep(Math.max(Step.ExcludeUsers + 1, progress))
          },
        }
      case Step.Finalize:
        return {
          onClick: () => onDone(competitor),
          isLast: true,
          disabled: competitor.name.trim() === '',
        }
      default:
        return null
    }
  })()

  return (
    <div className="p-3">
      <Stepper activeStep={step - 1} alternativeLabel className="wizard-stepper">
        {stepLabels.map((label, index) => {
          const stepNumber = index + 1
          const canNavigate = progress >= stepNumber
          return (
            <MuiStep key={label}>
              <StepLabel
                style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                onClick={() => canNavigate && setStep(stepNumber)}
                data-testid={`step-${index}`}
              >
                {label}
              </StepLabel>
            </MuiStep>
          )
        })}
      </Stepper>
      <Grid container spacing={4} className="h-100 mt-3">
        <Grid item xs={7}>
          <div className="pt-2">
            {step === Step.Source && stepSource()}
            {step === Step.Professionality && stepProfessionality()}
            {step === Step.Settings && stepSettings()}
            {step === Step.ExcludeUsers && stepExcludeUsers()}
            {step === Step.Finalize && stepFinalize()}
          </div>
        </Grid>
        <Grid item xs={5} className="d-flex flex-column justify-content-start mt-4 h-100">
          <div className="wizard-preview">
            <CompetitorPreview
              competitor={competitor}
              progress={progress}
              step={step}
              sourceSelected={sourceSelected}
              levelSelected={levelSelected}
            />
          </div>
        </Grid>
      </Grid>
      {nextConfig && (
        <div className="d-flex justify-content-end mt-3">
          <Button
            disabled={nextConfig.disabled}
            onClick={nextConfig.onClick}
            variant="contained"
            data-testid="next-btn"
          >
            {t(`common.${nextConfig.isLast ? 'done' : 'next'}`)}
          </Button>
        </div>
      )}
    </div>
  )
}
